// src/styles/GlobalStyles.js
import { createGlobalStyle } from "styled-components";
import { SPINNER_CSS } from "../components/AppSpinner.mjs";

export const GlobalStyle = createGlobalStyle`
  ${SPINNER_CSS}
  /* Inter is the primary app font, loaded via index.html. */
  :root {
    /* ── Colour tokens (mirrored from theme.js so components can use either) ── */
    --primary: ${({ theme }) => theme.primary};
    --primaryHover: ${({ theme }) => theme.primaryHover};
    --secondary: ${({ theme }) => theme.secondary};
    --background: ${({ theme }) => theme.background};
    --bodyBg: ${({ theme }) => theme.bodyBg};
    --shell: ${({ theme }) => theme.shell};
    --surface: ${({ theme }) => theme.surface};
    --panel: ${({ theme }) => theme.panel};
    --hover: ${({ theme }) => theme.hover};
    --cardBg: ${({ theme }) => theme.cardBg};
    --cardBorder: ${({ theme }) => theme.cardBorder};
    --border: ${({ theme }) => theme.border};
    --divider: ${({ theme }) => theme.divider};
    --text: ${({ theme }) => theme.text};
    --textSecondary: ${({ theme }) => theme.textSecondary};
    --textMuted: ${({ theme }) => theme.textMuted};
    --error: ${({ theme }) => theme.errorText};
    --errorText: ${({ theme }) => theme.errorText};
    --errorBg: ${({ theme }) => theme.errorBg};
    --success: ${({ theme }) => theme.successText};
    --successText: ${({ theme }) => theme.successText};
    --warningText: ${({ theme }) => theme.warningText};

    /* ── --color-* aliases ──────────────────────────────────────────────
       The Labs components and the shared UI primitives were written against
       a --color-* namespace that was never defined, so every declaration
       using one was silently dropped by the browser. The tokens all exist
       under the theme's own names; these map the namespace onto them so the
       existing styles resolve, and stay theme-aware. */
    --color-primary: ${({ theme }) => theme.primary};
    --color-primary-hover: ${({ theme }) => theme.primaryHover};
    --color-primary-strong: ${({ theme }) => theme.primaryHover};
    --color-primary-subtle: color-mix(in srgb, ${({ theme }) => theme.primary} 22%, transparent);
    /* ExamNova's primary is a saturated indigo in both themes, so white is
       the readable foreground on a primary fill. */
    --color-primary-contrast: #ffffff;
    --color-focus: ${({ theme }) => theme.primary};

    /* theme.background is a gradient, so it cannot be a background-color;
       the canvas token is the flat page colour. */
    --color-canvas: ${({ theme }) => theme.bodyBg};
    --color-shell: ${({ theme }) => theme.shell};
    --color-surface: ${({ theme }) => theme.surface};
    --color-surface-elevated: ${({ theme }) => theme.cardBg};
    --color-panel: ${({ theme }) => theme.panel};
    --color-inset: ${({ theme }) => theme.surface};

    --color-border: ${({ theme }) => theme.border};
    --color-border-subtle: ${({ theme }) => theme.divider};
    --color-border-strong: color-mix(in srgb, ${({ theme }) => theme.border} 62%, ${({ theme }) => theme.text});

    --color-text: ${({ theme }) => theme.text};
    --color-text-strong: ${({ theme }) => theme.text};
    --color-text-secondary: ${({ theme }) => theme.textSecondary};
    --color-text-muted: ${({ theme }) => theme.textMuted};

    --color-error: ${({ theme }) => theme.errorText};
    --color-error-bg: ${({ theme }) => theme.errorBg};
    --color-error-border: ${({ theme }) => theme.errorBorder};
    --color-success: ${({ theme }) => theme.successText};
    --color-success-bg: ${({ theme }) => theme.successBg};
    --color-success-border: ${({ theme }) => theme.successBorder};
    --color-warning: ${({ theme }) => theme.warningText};
    --color-warning-bg: ${({ theme }) => theme.warningBg};
    --color-warning-border: ${({ theme }) => theme.warningBorder};
    --color-info: ${({ theme }) => theme.infoText};
    --color-info-bg: ${({ theme }) => theme.infoBg};
    --color-info-border: ${({ theme }) => theme.infoBorder};
    --warning: ${({ theme }) => theme.warningText};

    /* ── Spacing, weight, control and layer scales ──────────────────────
       The Labs components reach for these too. Where a component wrote a
       fallback it agreed on the value every time - 8/12/16/20/24/32px, and
       600/700/900 - so these are the authors' own numbers rather than a
       scale invented here. The references without a fallback, which is most
       of the font weights, were being dropped entirely. */
    --space-1: 4px;
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;
    --space-5: 20px;
    --space-6: 24px;
    --space-7: 28px;
    --space-8: 32px;

    --font-weight-semibold: 600;
    --font-weight-bold: 700;
    --font-weight-extrabold: 800;
    --font-weight-black: 900;

    --control-height-compact: 32px;
    --control-height: 40px;
    --control-height-comfortable: 48px;
    --density-panel-padding: 16px;

    --z-sticky: 40;
    --z-action-bar: 500;

    /* ── Type and motion the Labs surface styles against ────────────────
       These were defined inside one component's wrapper, so they were only
       in scope for that component's subtree. Everywhere else the Labs used
       them - fourteen files for the sans stack, eleven for the mono one -
       the declaration was dropped, which is why output panels rendered in
       the body font rather than monospace. */
    --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      Oxygen, Ubuntu, Cantarell, sans-serif;
    --font-mono: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas,
      'Liberation Mono', monospace;
    --duration-fast: var(--dur-fast);
    --duration-normal: var(--dur);
    --ease-standard: var(--ease);

    /* ── Radius scale (collapses the old 4/6/8/10/12/16 sprawl to 4 steps) ── */
    --radius-xs: 6px;     /* chips, badges, tiny controls */
    --radius-sm: 8px;     /* buttons, inputs, option rows */
    --radius-md: 12px;    /* cards, modals, panels */
    --radius-lg: 16px;    /* large feature surfaces */
    --radius-pill: 999px; /* pills, progress tracks */

    /* ── Elevation scale (calm, neutral; tuned for the dark shell) ── */
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.18);
    --shadow-md: 0 6px 20px rgba(0, 0, 0, 0.22);
    --shadow-lg: 0 18px 44px rgba(0, 0, 0, 0.34);

    /* ── Motion (specific + quick; no "transition: all" sprawl) ── */
    --ease: cubic-bezier(0.4, 0, 0.2, 1);
    --dur-fast: 0.12s;
    --dur: 0.16s;
  }
  * {
    box-sizing: border-box;
  }

  html {
    background: ${({ theme }) => theme.bodyBg};
    scrollbar-gutter: stable;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    min-height: 100vh;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    transition: background 0.5s ease, color 0.5s ease;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    margin-top: 0;
    /* Large headings read tighter and more deliberate. */
    letter-spacing: -0.01em;
  }

  button, select, input, textarea {
    font-family: inherit;
  }

  /* Visible keyboard focus everywhere, on the app accent. Components may
     override with their own :focus-visible; this is the safe default that
     covers the many inline-styled controls that previously had none. */
  :focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  /* Quiet, theme-aware scrollbars — a small "considered" detail, app-wide. */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.28) transparent;
  }
  *::-webkit-scrollbar { width: 9px; height: 9px; }
  *::-webkit-scrollbar-track { background: transparent; }
  *::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.24);
    border-radius: var(--radius-pill);
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  *::-webkit-scrollbar-thumb:hover { background: rgba(148, 163, 184, 0.4); background-clip: padding-box; }

  ::selection {
    background: ${({ theme }) => theme.primary}33;
    color: ${({ theme }) => theme.text};
  }

  /* Respect users who reduce motion: keep the app usable, drop the animation. */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;
